import { Button, Label, NumberField } from "@heroui/react";
import { useId, useState } from "react";

import { AddressField } from "@/src/components/AddressField";
import { ChevronLeftIcon } from "@/src/components/icons";
import { useTranslations } from "@/src/i18n/i18n";
import type { GeocodingResult } from "@/src/libs/geocoding";

type GeoFilterPanelProps = {
  /** Le lieu tel qu'il a été choisi — vide quand aucun centre n'est posé. */
  place: string;
  hasCenter: boolean;
  radius: number;
  onSelect: (_place: GeocodingResult) => void;
  onRadiusChange: (_radius: number) => void;
  onClear: () => void;
};

/** Le plafond du serveur, identique sur les comptes et les événements. */
export const MAX_RADIUS_KM = 500;

/**
 * « Autour d'un lieu » : une adresse géocodée côté client, et un rayon.
 *
 * ⚠️ **Le géocodage est côté client**, contre Photon (OpenStreetMap, sans clé) :
 * le serveur ne connaît que des coordonnées, et c'est très bien ainsi — il n'a
 * ni clé d'API à porter, ni cache à tenir.
 *
 * ⚠️ **Un dépliant écrit ici, et non le `Disclosure` de HeroUI.** Celui-ci pose
 * `overflow: clip` sur son panneau pour animer sa hauteur : la liste de
 * suggestions, qui doit survoler ce qui suit, s'y faisait trancher —
 * `overflow-visible` en utilitaire n'y suffisait pas. Le comportement
 * d'accessibilité tient en trois attributs, les voici.
 *
 * Partagé par la liste des comptes et celle des événements : les deux routes
 * prennent le même trio `lat`/`lng`/`radius`, et deux panneaux séparés
 * finiraient par ne plus se comporter pareil.
 */
export function GeoFilterPanel({
  place,
  hasCenter,
  radius,
  onSelect,
  onRadiusChange,
  onClear,
}: GeoFilterPanelProps) {
  const t = useTranslations("geo");

  // Ouvert d'emblée si un lieu est déjà filtré : arriver par une URL partagée
  // et ne pas voir *où* l'on cherche serait la pire des surprises.
  const [isOpen, setOpen] = useState(hasCenter);
  const panelId = useId();

  return (
    <div className="rounded-lg border border-separator px-4">
      <h2>
        {/*
          ⚠️ **Pas de fond au survol.** `.button--ghost` pose
          `--button-bg-hover: var(--default)` : sur une bande large de toute la
          carte, ça donne un aplat gris qui se lit comme une sélection, pas
          comme un pointage. Ce sont bien les variables qu'on neutralise et non
          un `bg-*` — les variantes lisent `--button-bg`, et un `bg-*` laisserait
          le fond d'origine repasser devant au survol. Le pointage reste dit par
          le curseur, et le focus garde son anneau.
        */}
        <Button
          aria-controls={panelId}
          aria-expanded={isOpen}
          className="w-full justify-between py-3 text-sm text-muted [--button-bg-hover:transparent] [--button-bg-pressed:transparent]"
          variant="ghost"
          onPress={() => setOpen((open) => !open)}
        >
          {hasCenter
            ? t("active", { place: place || t("point"), radius })
            : t("open")}
          <ChevronLeftIcon
            className={`size-4 transition-transform ${
              isOpen ? "rotate-90" : "-rotate-90"
            }`}
          />
        </Button>
      </h2>

      {isOpen ? (
        <div className="flex flex-col gap-3 pb-4" id={panelId}>
          {/* `items-end` : les trois contrôles s'alignent par leur base, donc
              par leur champ — les libellés au-dessus n'ont pas la même hauteur
              de ligne. Ce qui déborde du champ d'adresse (liste, message) est
              sorti du flux, sinon il décalerait cet alignement à chaque
              frappe. */}
          <div className="flex flex-wrap items-end gap-3">
            {/*
              ⚠️ La `key` remonte le champ quand le lieu change depuis
              l'extérieur — un retour arrière du navigateur, par exemple. Sans
              elle, le texte affiché resterait celui de la frappe précédente
              alors que le filtre appliqué a changé.
            */}
            <AddressField
              initialLabel={place}
              key={place}
              onSelect={onSelect}
            />

            <NumberField
              className="w-32"
              maxValue={MAX_RADIUS_KM}
              minValue={1}
              value={radius}
              onChange={onRadiusChange}
            >
              <Label>{t("radius")}</Label>
              {/*
                ⚠️ **`grid-cols-1`, sinon le champ n'affiche que deux chiffres.**
                `.number-field__group` est une grille `40px 1fr 40px` : les deux
                colonnes latérales sont réservées aux boutons `+` / `−`, qu'on
                ne rend pas. Sur 128 px, l'input n'en gardait que 40 — 16 px de
                texte une fois le rembourrage retiré, et « 250 » s'affichait
                « 50 ». Sans stepper, une seule colonne.
              */}
              <NumberField.Group className="grid-cols-1">
                <NumberField.Input />
              </NumberField.Group>
            </NumberField>

            {hasCenter ? (
              <Button size="sm" variant="ghost" onPress={onClear}>
                {t("clear")}
              </Button>
            ) : null}
          </div>

          <p className="text-xs text-pretty text-muted">{t("note")}</p>
        </div>
      ) : null}
    </div>
  );
}
